import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import * as AutoOrderActions from './auto-ordering.actions';
import * as UserActions from '../../user-access/user-store/user.actions';
import {
  AutoOrderFilterOrderSettingsDefaults,
  AutoOrderListStatusChipsDefaults,
  DEFAULT_AUTO_ORDER_PROGRESS_STEP,
  DEFAULT_OPEN_AUTO_GENERATED_ORDER,
  IAutoOrderAddStockItems,
  IAutoOrderDisableItemOutcome,
  IAutoOrderFilterOrderSettings,
  IAutoOrderListStatusChips,
  IAutoOrderOrder,
  IAutoOrderOrderItem,
  IAutoOrderOrderProgressStep,
  IAutoOrderOrderStockItem,
  IAutoOrderPrepareOutcome,
  IAutoOrderSupplierEmails,
  IOpenAutoGeneratedOrder,
} from '../models';
import {StockItem, StockItemUpdateFieldValue} from '../../../shared-utilities/models-old/datastructures';
import {IDisableStockItemRule, IOnHandNotZeroDisableRule, IOnHandZeroDisableRule} from '../../settings-new/models';
import {IFirebaseQuery} from '../../../shared/shared-models/firebase/firebase-queries';
import {countAutoOrderCompletedSteps} from '../utils/open-auto-order.utils';
import {shouldClearPendingRetryAutoOrder, patchAutoOrderAfterRetrySubmit} from '../utils/auto-order-failure-recovery.utils';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IAutoOrderOrderingSettings, IAutoOrderExpirationSettings} from '../../settings-new/models';
import {AOScheduleElement} from '../models/old-models/ao-datastructures';

/**
 * Interface for tracking schedule loading state per store
 */
export interface IScheduleLoadingState {
  daysLoaded: string[];
  error: IGlobalError | null;
  expectedDayCount: number;
  isLoading: boolean;
  suppliersLoaded: boolean;
}

export interface AutoOrderingState {
  addStockItemsData: IAutoOrderAddStockItems;
  autoOrderLatestFirebaseQueries: IFirebaseQuery[];
  autoOrderOrderSettings: Record<string, IAutoOrderOrderingSettings>;
  autoOrderOrders: Record<string, IAutoOrderOrder[]>;
  autoOrderScheduleData: Record<string, Record<string, Record<string, AOScheduleElement>>>;
  autoOrderStatusChips: IAutoOrderListStatusChips;
  autoOrderStockItems: Record<string, Record<string, Record<string, StockItem[]>>>;
  autoOrderStockItemsStreamCount: number;
  autoOrderingOrderFilters: IAutoOrderFilterOrderSettings;
  /** Id of the stock take created from the open-order uncounted list. */
  createdOrderStockTakeId: string | null;
  disabledRules: Record<string, IDisableStockItemRule>;
  errors: IGlobalError[];
  isAutoOrderDeleteOrderLoading: boolean;
  isAutoOrderOrderItemsLoading: boolean;
  isAutoOrderOrderSettingsLoading: boolean;
  isAutoOrderOrderStockItemsLoading: boolean;
  isAutoOrderOrdersLoading: boolean;
  isAutoOrderScheduleDeleteSettingsLoading: boolean;
  isAutoOrderScheduleLoading: boolean;
  isAutoOrderStockDeletionRulesLoading: boolean;
  /** True while an open-order uncounted list is creating a stock take. */
  isCreatingOrderStockTake: boolean;
  isDeleteAutoOrderDoNotAskAgainActive: boolean;
  isOpenOrderAddItemsLoading: boolean;
  isOpenOrderLoading: IAutoOrderOrderProgressStep;
  isOpenOrderRemoveItemsLoading: boolean;
  isOpenOrderSetItemTagsLoading: boolean;
  isSupplierEmailsLoading: boolean;
  isUpdatingStockItemDisabled: boolean;
  isUpdatingStockItemOnHold: boolean;
  lastScheduleUpdateTimestamp: number | null;
  openOrder: Record<string, IOpenAutoGeneratedOrder>;
  orderListSortBy: {property: string; direction: 'asc' | 'desc'};
  pendingOpenAutoOrder: {orderId: string; storeId: string; order: IAutoOrderOrder | null; requestedAt: number} | null;
  pendingRetryAutoOrderId: string | null;
  prepareOrderOutcome: IAutoOrderPrepareOutcome | null;
  preparingOrderIds: string[];
  scheduleDeleteSettings: IAutoOrderExpirationSettings;
  scheduleLoadingState: Record<string, IScheduleLoadingState>;
  selectedAutoOrderIds: string[];
  selectedAutoOrderItems: IAutoOrderOrderItem[];
  stockItemDisableBaselines: Record<string, Record<string, StockItemUpdateFieldValue>>;
  stockItemDisabledOutcome: IAutoOrderDisableItemOutcome | null;
  stockItemOnHoldUpdateFailed: boolean;
  supplierEmails: Record<string, Record<string, IAutoOrderSupplierEmails>>;
}

export const initialAutoOrderingState: AutoOrderingState = {
  isOpenOrderLoading: {...DEFAULT_AUTO_ORDER_PROGRESS_STEP},
  isOpenOrderAddItemsLoading: false,
  isOpenOrderRemoveItemsLoading: false,
  isOpenOrderSetItemTagsLoading: false,
  isSupplierEmailsLoading: false,
  openOrder: {},
  pendingOpenAutoOrder: null,
  pendingRetryAutoOrderId: null,
  selectedAutoOrderIds: [],
  supplierEmails: {},
  // Initial --------------------------------
  errors: [],
  // Auto Ordering --------------------------
  addStockItemsData: {} as IAutoOrderAddStockItems,
  autoOrderLatestFirebaseQueries: [],
  autoOrderOrders: {},
  autoOrderOrderSettings: {},
  autoOrderScheduleData: {},
  autoOrderStatusChips: {...AutoOrderListStatusChipsDefaults},
  scheduleLoadingState: {},
  autoOrderStockItemsStreamCount: 0,
  autoOrderStockItems: {},
  autoOrderingOrderFilters: {...AutoOrderFilterOrderSettingsDefaults},
  disabledRules: {},
  isAutoOrderDeleteOrderLoading: false,
  isAutoOrderOrdersLoading: false,
  isAutoOrderOrderItemsLoading: false,
  isAutoOrderOrderSettingsLoading: false,
  isAutoOrderOrderStockItemsLoading: false,
  isAutoOrderScheduleDeleteSettingsLoading: false,
  isAutoOrderScheduleLoading: false,
  isAutoOrderStockDeletionRulesLoading: false,
  isDeleteAutoOrderDoNotAskAgainActive: false,
  isUpdatingStockItemDisabled: false,
  isUpdatingStockItemOnHold: false,
  isCreatingOrderStockTake: false,
  createdOrderStockTakeId: null,
  stockItemDisableBaselines: {},
  stockItemDisabledOutcome: null,
  stockItemOnHoldUpdateFailed: false,
  lastScheduleUpdateTimestamp: null,
  scheduleDeleteSettings: null,
  selectedAutoOrderItems: [],
  orderListSortBy: {property: 'generated', direction: 'asc'},
  prepareOrderOutcome: null,
  preparingOrderIds: [],
};

const createAutoOrderingReducer: ActionReducer<AutoOrderingState> = createReducer(
  {...initialAutoOrderingState},
  //#region USER ACTIONS
  on(UserActions.logOutUser, (state: AutoOrderingState) => ({
    ...initialAutoOrderingState,
  })),
  //#endregion
  //#region GLOBAL
  on(AutoOrderActions.setPropertyOnAutoOrderState, (state: AutoOrderingState, {property, value}) => ({
    ...state,
    [property]: value,
  })),
  on(AutoOrderActions.setSupplierEmailsLoading, (state: AutoOrderingState, {isLoading}) => ({
    ...state,
    isSupplierEmailsLoading: isLoading,
  })),
  on(AutoOrderActions.clearAutoOrderingState, (state: AutoOrderingState) => ({
    ...initialAutoOrderingState,
  })),
  //#endregion
  //#region OPEN AUTO ORDER - GET AND INITIALIZE
  on(AutoOrderActions.setOpenOrderLoadingIndicator, (state: AutoOrderingState, {property, value}) => ({
    ...state,
    isOpenOrderLoading: {
      ...state.isOpenOrderLoading,
      [property]: value,
    },
  })),
  on(AutoOrderActions.closeAutoOrder, (state: AutoOrderingState) => ({
    ...state,
    isOpenOrderLoading: {...DEFAULT_AUTO_ORDER_PROGRESS_STEP},
    openOrder: {},
    selectedAutoOrderIds: [],
  })),
  on(AutoOrderActions.openAutoOrder, (state: AutoOrderingState, {orderId}) => ({
    ...state,
    isOpenOrderLoading: {
      ...state.isOpenOrderLoading,
      openAutoOrderOrderStarted: true,
      openAutoOrderOrderCompleted: true,
      getSelectedStore: true,
      setSelectedOrder: true,
      setSelectedSupplier: true,
    },
    openOrder: orderId
      ? {[orderId]: {...DEFAULT_OPEN_AUTO_GENERATED_ORDER}}
      : {},
  })),
  on(AutoOrderActions.initializeOpenAutoOrder, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getSelectedStore: false,
        setSelectedOrder: false,
        setSelectedSupplier: false,
      },
      openOrder: orderId ? {[orderId]: progress} : {},
    };
  }),
  on(AutoOrderActions.getOrderItemsForOpenAutoOrder, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder[orderId] : undefined;

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getOrderItems: true,
        concatenateStockItems: true,
        deDuplicateStockItems: true,
      },
      openOrder: orderId && existingOrder
        ? {
          [orderId]: {
            ...existingOrder,
            steps: existingOrder.steps,
          },
        }
        : {},
    };
  }),
  on(AutoOrderActions.getOrderItemsForOpenAutoOrderSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder[orderId] : undefined;
    if (!orderId || !existingOrder) {
      return state;
    }
    const updatedOrder = {
      ...existingOrder,
      orderItems: progress.orderItems,
      orderItemsAdded: progress.orderItemsAdded,
      steps: {
        ...existingOrder.steps,
        getOrderItems: true,
      },
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(existingOrder.steps),
        existingOrder.totalSteps,
      ),
    };
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getOrderItems: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.getStockItemsForOrderRegularSupplier, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder[orderId] : undefined;
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getStockItemsRegularSupplier: true,
      },
      openOrder: orderId && existingOrder
        ? {
          [orderId]: {
            ...existingOrder,
            steps: existingOrder.steps,
          },
        }
        : {},
    };
  }),
  on(AutoOrderActions.getStockItemsForOrderRegularSupplierSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const supplierStockItems = {
      additional: [],
      added: [],
      main: [],
      ...(existingOrder.supplierStockItems ?? {}),
      ...(progress?.supplierStockItems?.main ? {main: progress.supplierStockItems.main} : {}),
    };

    const steps = {
      ...existingOrder.steps,
      getStockItemsRegularSupplier: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      supplierStockItems,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getStockItemsRegularSupplier: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.getStockItemsForOrderAdditionalSuppliers, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getStockItemsAdditionalSupplier: true,
      },
      openOrder: orderId && existingOrder
        ? {
          [orderId]: {
            ...existingOrder,
            steps: existingOrder.steps,
          },
        }
        : {},
    };
  }),
  on(AutoOrderActions.getStockItemsForOrderAdditionalSuppliersSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const supplierStockItems = {
      additional: [],
      added: [],
      main: [],
      ...(existingOrder.supplierStockItems ?? {}),
      ...(progress?.supplierStockItems?.additional
        ? {additional: progress.supplierStockItems.additional}
        : {}),
    };

    const steps = {
      ...existingOrder.steps,
      getStockItemsAdditionalSupplier: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      supplierStockItems,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getStockItemsAdditionalSupplier: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.getStockItemsForAddedItemsToOrder, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;
    const supplierStockItems = existingOrder.supplierStockItems ?? {
      additional: [],
      added: [],
      main: [],
    };
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getOrderItemsAdded: true,
      },
      openOrder: {
        [orderId]: {
          ...existingOrder,
          steps: existingOrder.steps,
          supplierStockItems,
        },
      },
    };
  }),
  on(AutoOrderActions.getStockItemsForAddedItemsToOrderSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const supplierStockItems = {
      additional: [],
      added: [],
      main: [],
      ...(existingOrder.supplierStockItems ?? {}),
      ...(progress?.supplierStockItems?.added ? {added: progress.supplierStockItems.added} : {}),
    };

    const steps = {
      ...existingOrder.steps,
      getOrderItemsAdded: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      orderItemsAdded: progress.orderItemsAdded,
      supplierStockItems,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getOrderItemsAdded: !progress.steps.getOrderItemsAdded,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.groupStockItemsByCodeForOpenAutoOrder, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const steps = {
      ...existingOrder.steps,
      concatenateStockItems: true,
      deDuplicateStockItems: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      stockItems: progress.stockItems,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        concatenateStockItems: false,
        deDuplicateStockItems: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.getMissingStockCodesAndOrderItemsOnOpenOrder, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        determineMissingStockCodes: true,
        determineMissingOrderItemCodes: true,
      },
      openOrder: {
        [orderId]: {
          ...existingOrder,
          ...progress,
          steps: existingOrder.steps,
        },
      },
    };
  }),
  on(AutoOrderActions.getMissingStockCodesAndOrderItemsOnOpenOrderSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const steps = {
      ...existingOrder.steps,
      determineMissingStockCodes: true,
      determineMissingOrderItemCodes: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      missing: progress.missing,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        determineMissingStockCodes: false,
        determineMissingOrderItemCodes: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.getEditedOrderStockItemsFromLocalStorage, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;
    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getEditedStockItemsFromLocalStorage: true,
      },
      openOrder: {
        [orderId]: {
          ...existingOrder,
          ...progress,
          steps: existingOrder.steps,
        },
      },
    };
  }),
  on(AutoOrderActions.getEditedOrderStockItemsFromLocalStorageSuccess, (state: AutoOrderingState, {progress}) => {
    const orderId = progress?.orderDetails?.orderId;
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const steps = {
      ...existingOrder.steps,
      getEditedStockItemsFromLocalStorage: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      localStorageEditedItems: progress.localStorageEditedItems,
      localStorageStreams: progress.localStorageStreams,
      steps,
      progress: calculatePercentile(
        countAutoOrderCompletedSteps(steps),
        existingOrder.totalSteps,
      ),
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        getEditedStockItemsFromLocalStorage: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  on(AutoOrderActions.openAutoOrderSuccess, (state: AutoOrderingState, {orderId, success}) => {
    const existingOrder = orderId ? state.openOrder?.[orderId] : undefined;
    if (!orderId || !existingOrder) return state;

    const steps = {
      ...existingOrder.steps,
      openAutoOrderOrderCompleted: true,
    };

    const updatedOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      steps,
      progress: success === false
        ? 100
        : calculatePercentile(countAutoOrderCompletedSteps(steps), existingOrder.totalSteps),
      success,
    };

    return {
      ...state,
      isOpenOrderLoading: {
        ...state.isOpenOrderLoading,
        openAutoOrderOrderStarted: false,
        openAutoOrderOrderCompleted: false,
      },
      openOrder: {
        [orderId]: updatedOrder,
      },
    };
  }),
  //#endregion
  //#region OPEN AUTO ORDER - ADD ITEMS TO ORDER
  on(AutoOrderActions.addItemsToOpenOrder, (state: AutoOrderingState) => ({
    ...state,
    isOpenOrderAddItemsLoading: true,
  })),
  on(AutoOrderActions.addItemsToOpenOrderSuccess, (state: AutoOrderingState) => {
    return {
      ...state,
      isOpenOrderAddItemsLoading: false,
    };
  }),
  on(AutoOrderActions.setStockItemTagsForAddedOpenOrderItems, (state: AutoOrderingState) => ({
    ...state,
    isOpenOrderSetItemTagsLoading: true,
  })),
  on(AutoOrderActions.setStockItemTagsForAddedOpenOrderItemsSuccess, (state: AutoOrderingState) => {
    return {
      ...state,
      isOpenOrderSetItemTagsLoading: false,
    };
  }),
  //#endregion
  //#region OPEN AUTO ORDER - REMOVE ITEMS FROM ORDER
  on(AutoOrderActions.removeItemsFromOpenOrder, (state: AutoOrderingState) => ({
    ...state,
    isOpenOrderRemoveItemsLoading: true,
  })),
  on(AutoOrderActions.removeItemsFromOpenOrderSuccess, (state: AutoOrderingState, {orderId, codesToRemove}) => {
    const existingOrder = state.openOrder[orderId] || {...DEFAULT_OPEN_AUTO_GENERATED_ORDER};

    const openOrder: IOpenAutoGeneratedOrder = {
      ...existingOrder,
      missing: {
        ...existingOrder.missing,
        stockItemMissingNewFalse: [] as IAutoOrderOrderItem[],
        orderItemMissing: [] as IAutoOrderOrderStockItem[],
      },
    };

    return {
      ...state,
      isOpenOrderRemoveItemsLoading: false,
      openOrder: {
        ...state.openOrder,
        [orderId]: openOrder,
      },
    };
  }),
  //#endregion
  //#region AUTO ORDER - UPDATE ORDER PROPERTY ON STATE
  on(AutoOrderActions.updateStateAutoOrderOrder, (state: AutoOrderingState, {orderIds, store}) => {
    const existingOrders = state.autoOrderOrders[store.storeId] || [];
    const orderIdsSet = new Set(orderIds);

    const stateOrdersAtStore = existingOrders.map((order: IAutoOrderOrder): IAutoOrderOrder =>
      orderIdsSet.has(order.orderId)
        ? {...order, status: 'SUBMITTED'}
        : order,
    );

    return {
      ...state,
      autoOrderOrders: {
        ...state.autoOrderOrders,
        [store.storeId]: stateOrdersAtStore,
      },
    };
  }),
  //#endregion
  // ====================================================================================================
  // Send Orders
  // ====================================================================================================
  on(AutoOrderActions.setRetryAutoOrder, (state: AutoOrderingState, {order}) => ({
    ...state,
    pendingRetryAutoOrderId: order.orderId,
  })),
  on(AutoOrderActions.setRetryAutoOrderSuccess, (state: AutoOrderingState) =>
    applyRetryAutoOrderSuccess(state),
  ),
  on(AutoOrderActions.syncPendingRetryAutoOrderFromFirestore, (state: AutoOrderingState, {storeId, order}) => ({
    ...state,
    pendingRetryAutoOrderId: null,
    autoOrderOrders: {
      ...state.autoOrderOrders,
      [storeId]: mergeAutoOrderIntoStoreList(state.autoOrderOrders[storeId] ?? [], order),
    },
    openOrder: mergeRetriedAutoOrderIntoOpenOrder(state.openOrder, order),
  })),
  on(AutoOrderActions.setRetryAutoOrderFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    pendingRetryAutoOrderId: null,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.updateAutoOrderStatusField, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrdersLoading: true,
  })),
  on(AutoOrderActions.updateAutoOrderStatusFieldSuccess, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
  })),
  on(AutoOrderActions.updateAutoOrderStatusFieldFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.submitAutoOrders, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrdersLoading: true,
  })),
  on(AutoOrderActions.submitAutoOrdersSuccess, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
  })),
  on(AutoOrderActions.submitAutoOrdersFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
    errors: [...state.errors, error],
  })),
  // ====================================================================================================
  // Auto Order Orders
  // ====================================================================================================
  on(AutoOrderActions.setAutoOrderListSortingOrder, (state: AutoOrderingState, {sortBy}) => ({
    ...state,
    orderListSortBy: sortBy,
  })),
  on(AutoOrderActions.setAutoOrderSelectedOrders, (state: AutoOrderingState, {orderIds}) => ({
    ...state,
    selectedAutoOrderIds: orderIds,
  })),
  on(AutoOrderActions.requestOpenAutoOrderFromNotification, (state: AutoOrderingState, {orderId, storeId, order}) => {
    const pendingOpenAutoOrder: AutoOrderingState['pendingOpenAutoOrder'] = {
      orderId,
      storeId,
      order: order ?? null,
      requestedAt: ++pendingOpenAutoOrderRequest,
    };
    return {
      ...state,
      pendingOpenAutoOrder,
      autoOrderOrders: order
        ? {
          ...state.autoOrderOrders,
          [storeId]: mergePendingOpenOrder(
            state.autoOrderOrders[storeId] ?? [],
            pendingOpenAutoOrder,
            storeId,
          ),
        }
        : state.autoOrderOrders,
    };
  }),
  on(AutoOrderActions.clearPendingOpenAutoOrder, (state: AutoOrderingState) => ({
    ...state,
    pendingOpenAutoOrder: null,
  })),
  on(AutoOrderActions.getAutoOrderOrders, (state: AutoOrderingState, {statusChip, firebaseQueries}) => ({
    ...state,
    isAutoOrderOrdersLoading: true,
    autoOrderLatestFirebaseQueries: firebaseQueries,
    autoOrderStatusChips: statusChip,
    autoOrderOrders: {},
  })),
  on(AutoOrderActions.setAutoOrderChipStatus, (state: AutoOrderingState, {chips}) => ({
    ...state,
    autoOrderStatusChips: chips,
  })),
  on(AutoOrderActions.getAutoOrderOrdersSuccess, (state: AutoOrderingState, {
    autoOrderOrders,
    store,
    statusChip,
    firebaseQueries,
  }) => {
    const mergedOrders: IAutoOrderOrder[] = mergePendingOpenOrder(
      autoOrderOrders,
      state.pendingOpenAutoOrder,
      store.storeId,
    );
    const pendingRetryAutoOrderId: string | null = shouldClearPendingRetryAutoOrder(
      state.pendingRetryAutoOrderId,
      mergedOrders,
    )
      ? null
      : state.pendingRetryAutoOrderId;
    const openOrder: Record<string, IOpenAutoGeneratedOrder> = syncRetriedOpenOrder(
      state.openOrder,
      mergedOrders,
      state.pendingRetryAutoOrderId,
    );
    return {
      ...state,
      isAutoOrderOrdersLoading: false,
      autoOrderLatestFirebaseQueries: firebaseQueries,
      autoOrderStatusChips: statusChip,
      pendingRetryAutoOrderId,
      openOrder,
      autoOrderOrders: {
        ...state.autoOrderOrders,
        [store.storeId]: mergedOrders,
      },
    };
  }),
  on(AutoOrderActions.getAutoOrderOrdersFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.setAutoOrderingOrderFilter, (state: AutoOrderingState, {filter}) => ({
    ...state,
    autoOrderingOrderFilters: filter,
  })),
  // ====================================================================================================
  // Delete Auto Orders
  // ====================================================================================================
  on(AutoOrderActions.deleteAutoOrderOrder, (state: AutoOrderingState, {doNotAskAgain}) => ({
    ...state,
    isAutoOrderOrdersLoading: true,
    isAutoOrderDeleteOrderLoading: true,
    isDeleteAutoOrderDoNotAskAgainActive: doNotAskAgain,
    selectedAutoOrderIds: [],
  })),
  on(AutoOrderActions.deleteAutoOrderOrderWithStoreId, (state: AutoOrderingState) => {
    return {
      ...state,
    };
  }),
  on(AutoOrderActions.deleteAutoOrderOrderSuccess, (state: AutoOrderingState, {orderIds, store}) => {
    const existingOrders = state.autoOrderOrders[store.storeId] || [];
    const orderIdsSet = new Set(orderIds);

    const storeOrders: IAutoOrderOrder[] = existingOrders.map((order: IAutoOrderOrder): IAutoOrderOrder =>
      orderIdsSet.has(order.orderId)
        ? {...order, status: 'DELETING'}
        : order,
    );

    return {
      ...state,
      isAutoOrderOrdersLoading: false,
      isAutoOrderDeleteOrderLoading: false,
      autoOrderOrders: {
        ...state.autoOrderOrders,
        [store.storeId]: storeOrders,
      },
    };
  }),
  on(AutoOrderActions.deleteAutoOrderOrderFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderOrdersLoading: false,
    isAutoOrderDeleteOrderLoading: false,
    error: [...state.errors, error],
  })),
  // ====================================================================================================
  // Schedule Auto Delete Auto Orders Settings
  // ====================================================================================================
  on(AutoOrderActions.getAutoOrderAutoDeleteSettings, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderScheduleDeleteSettingsLoading: true,
  })),
  on(AutoOrderActions.getAutoOrderAutoDeleteSettingsSuccess, (state: AutoOrderingState, {settings}) => ({
    ...state,
    isAutoOrderScheduleDeleteSettingsLoading: false,
    scheduleDeleteSettings: settings,
  })),
  on(AutoOrderActions.getAutoOrderAutoDeleteSettingsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderScheduleDeleteSettingsLoading: false,
    error: [...state.errors, error],
  })),
  // ====================================================================================================
  // Get Auto Order Stock Items
  // ====================================================================================================
  on(AutoOrderActions.getAutoOrderStockItems, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrderStockItemsLoading: true,
    autoOrderStockItemsStreamCount: state.autoOrderStockItemsStreamCount + 1,
  })),
  on(AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStore, (state: AutoOrderingState, {store, orderId}) => ({
    ...state,
    isAutoOrderOrderStockItemsLoading: true,
    autoOrderStockItems: {
      ...state.autoOrderStockItems,
      [store.storeId]: {
        ...state.autoOrderStockItems[store.storeId],
        [orderId]: {},
      },
    },
  })),
  on(AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStoreSuccess, (state: AutoOrderingState, {
    store,
    stockItems,
    orderId,
    stream,
  }) => ({
    ...state,
    isAutoOrderOrderStockItemsLoading: false,
    autoOrderStockItemsStreamCount: state.autoOrderStockItemsStreamCount - 1,
    autoOrderStockItems: {
      ...state.autoOrderStockItems,
      [store.storeId]: {
        ...state.autoOrderStockItems[store.storeId],
        [orderId]: {
          ...state.autoOrderStockItems[store.storeId][orderId],
          [stream]: stockItems,
        },
      },
    },
  })),
  on(AutoOrderActions.getAutoOrderStockItemsWithUserSelectedStoreFailure, (state: AutoOrderingState, {
    store,
    error,
    orderId,
  }) => ({
    ...state,
    isAutoOrderOrderStockItemsLoading: false,
    autoOrderStockItemsStreamCount: state.autoOrderStockItemsStreamCount - 1,
    autoOrderStockItems: {
      ...state.autoOrderStockItems,
      [store.storeId]: {
        ...state.autoOrderStockItems[store.storeId],
        [orderId]: {},
      },
    },
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.updateItemPropertyOnAutoOrderSuccess, (state: AutoOrderingState, {
    stockCode,
    property,
    newValue,
    selectedOrderId,
    updateReducer,
  }) => {
    if (!updateReducer) {
      return state;
    }

    const openOrder = state.openOrder[selectedOrderId];
    if (!openOrder) return state;

    const stockItems = openOrder.missing.validStockItems.map((item) =>
      item.code === stockCode
        ? {...item, [property]: newValue}
        : item,
    );

    return {
      ...state,
      openOrder: {
        ...state.openOrder,
        [selectedOrderId]: {
          ...openOrder,
          missing: {
            ...openOrder.missing,
            validStockItems: stockItems,
          },
        },
      },
    };
  }),
  // ====================================================================================================
  // Stock Item Deletion Rules
  // ====================================================================================================
  on(AutoOrderActions.getAutoOrderStockItemDeletionRules, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderStockDeletionRulesLoading: true,
  })),
  on(AutoOrderActions.getAutoOrderStockItemDeletionRulesSuccess, (state: AutoOrderingState, {
    store,
    disabledRules,
  }) => ({
    ...state,
    isAutoOrderStockDeletionRulesLoading: false,
    disabledRules: {
      ...state.disabledRules,
      [store.storeId]: disabledRules,
    },
  })),
  on(AutoOrderActions.getAutoOrderStockItemDeletionRulesFailure, (state: AutoOrderingState, {
    store, error,
  }) => ({
    ...state,
    isAutoOrderStockDeletionRulesLoading: false,
    disabledRules: {
      ...state.disabledRules,
      [store.storeId]: {onHandZero: {} as IOnHandZeroDisableRule, onHandNotZero: {} as IOnHandNotZeroDisableRule},
    },
    errors: [...state.errors, error],
  })),
  // ====================================================================================================
  // Recipients and Suppliers
  // ====================================================================================================
  //#region AUTO ORDER - SUPPLIER EMAILS
  on(AutoOrderActions.clearAllAutoOrderSupplierEmails, (state: AutoOrderingState) => ({
    ...state,
    isSupplierEmailsLoading: false,
    supplierEmails: {},
  })),
  on(AutoOrderActions.getSpecifiedSupplierEmailsForSelectedStore, (state: AutoOrderingState) => ({
    ...state,
    isSupplierEmailsLoading: true,
    supplierEmails: {},
  })),
  on(AutoOrderActions.getSpecifiedSupplierEmailsForSelectedStoreSuccess, (state: AutoOrderingState, {
    emails,
    storeId,
  }) => {
    const storeEmails = {...state.supplierEmails[storeId]};
    emails.forEach((email: IAutoOrderSupplierEmails) => {
      storeEmails[email.id] = email;
    });
    return {
      ...state,
      isSupplierEmailsLoading: false,
      supplierEmails: {
        ...state.supplierEmails,
        [storeId]: storeEmails,
      },
    };
  }),
  //#endregion
  // ====================================================================================================
  // Order Settings
  // ====================================================================================================
  on(AutoOrderActions.getAutoOrderOrderSettings, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderOrderSettingsLoading: true,
  })),
  on(AutoOrderActions.setAutoOrderOrderSettings, (state: AutoOrderingState, {settings, storeId}) => ({
    ...state,
    isAutoOrderOrderSettingsLoading: false,
    autoOrderOrderSettings: {
      ...state.autoOrderOrderSettings,
      [storeId]: settings,
    },
  })),
  on(AutoOrderActions.getAutoOrderOrderSettingsSuccess, (state: AutoOrderingState, {settings, storeId}) => ({
    ...state,
    isAutoOrderOrderSettingsLoading: false,
    autoOrderOrderSettings: {
      ...state.autoOrderOrderSettings,
      [storeId]: settings,
    },
  })),
  on(AutoOrderActions.getAutoOrderOrderSettingsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderOrderSettingsLoading: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.getTypesenseStockItemsSuccess, (state: AutoOrderingState, {
    stockItems,
    paginationData,
  }) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      stockItems,
      paginationData,
    },
  })),
  on(AutoOrderActions.getTypesenseStockItemsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.getTypesenseStockItemsSearchResultsSuccess, (state: AutoOrderingState, {
    stockItems,
    paginationData,
    filters,
    facetCounts,
  }) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      stockItems,
      paginationData,
      facetCounts,
      filters,
    },
  })),
  on(AutoOrderActions.getTypesenseStockItemsSearchResultsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.getAddItemLineColoursSuccess, (state: AutoOrderingState, {
    lineColours,
  }) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      lineColour: lineColours,
    },
  })),
  on(AutoOrderActions.getAddItemLineColoursFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.getTypesenseFacetCounts, (state: AutoOrderingState) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      facetCounts: undefined,
    },
  })),
  on(AutoOrderActions.getTypesenseFacetCountsSuccess, (state: AutoOrderingState, {
    facetCounts,
  }) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      facetCounts,
    },
  })),
  on(AutoOrderActions.getTypesenseFacetCountsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.setStockSearchAppliedFilter, (state: AutoOrderingState, {filter}) => ({
    ...state,
    addStockItemsData: {
      ...state.addStockItemsData,
      filter,
    },
  })),
  // ====================================================================================================
  // Schedule Settings
  // ====================================================================================================
  on(AutoOrderActions.getAutoOrderingScheduleSettings, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderScheduleLoading: true,
  })),
  on(AutoOrderActions.getAutoOrderingScheduleSettingsSuccess, (state: AutoOrderingState, {storeId, dayCount}) => {
    // Success action is dispatched when subscriptions are set up
    // Individual day data will arrive via updateScheduleDayData actions
    return {
      ...state,
      isAutoOrderScheduleLoading: false,
    };
  }),
  on(AutoOrderActions.getAutoOrderingScheduleSettingsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.updateScheduleDayData, (state: AutoOrderingState, {storeId, day, scheduleData}) => {
    // Store schedule data for a specific day as it arrives from Firebase observables
    const storeSchedule = state.autoOrderScheduleData[storeId] || {};
    return {
      ...state,
      autoOrderScheduleData: {
        ...state.autoOrderScheduleData,
        [storeId]: {
          ...storeSchedule,
          [day]: scheduleData,
        },
      },
    };
  }),
  on(AutoOrderActions.updateAutoOrderingScheduleSettings, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderScheduleLoading: true,
  })),
  on(AutoOrderActions.updateAutoOrderingScheduleSettingsSuccess, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    lastScheduleUpdateTimestamp: Date.now(),
  })),
  on(AutoOrderActions.updateAutoOrderingScheduleSettingsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.getAutoOrderingScheduleForDay, (state: AutoOrderingState) => ({
    ...state,
    isAutoOrderScheduleLoading: true,
  })),
  on(AutoOrderActions.getAutoOrderingScheduleForDaySuccess, (state: AutoOrderingState, {storeId, day, scheduleDay}) => {
    // scheduleDay is Record<supplierId, AOScheduleElement>
    // Store it by storeId and day
    const storeSchedule = state.autoOrderScheduleData[storeId] || {};
    return {
      ...state,
      isAutoOrderScheduleLoading: false,
      autoOrderScheduleData: {
        ...state.autoOrderScheduleData,
        [storeId]: {
          ...storeSchedule,
          [day]: scheduleDay,
        },
      },
    };
  }),
  on(AutoOrderActions.getAutoOrderingScheduleForDayFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    errors: [...state.errors, error],
  })),
  // ====================================================================================================
  // Schedule Loading Orchestration Reducers
  // ====================================================================================================
  on(AutoOrderActions.loadScheduleForStore, (state: AutoOrderingState, {storeId}) => ({
    ...state,
    isAutoOrderScheduleLoading: true,
    scheduleLoadingState: {
      ...state.scheduleLoadingState,
      [storeId]: {
        isLoading: true,
        daysLoaded: [],
        suppliersLoaded: false,
        expectedDayCount: 0,
        error: null,
      },
    },
  })),
  on(AutoOrderActions.setScheduleLoadingProgress, (state: AutoOrderingState, {storeId, daysLoaded, suppliersLoaded, expectedDayCount}) => ({
    ...state,
    scheduleLoadingState: {
      ...state.scheduleLoadingState,
      [storeId]: {
        ...(state.scheduleLoadingState[storeId] || {
          isLoading: true,
          daysLoaded: [],
          suppliersLoaded: false,
          expectedDayCount: 0,
          error: null,
        }),
        daysLoaded,
        suppliersLoaded,
        expectedDayCount,
      },
    },
  })),
  on(AutoOrderActions.scheduleDataLoadingComplete, (state: AutoOrderingState, {storeId}) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    scheduleLoadingState: {
      ...state.scheduleLoadingState,
      [storeId]: {
        ...(state.scheduleLoadingState[storeId] || {
          isLoading: false,
          daysLoaded: [],
          suppliersLoaded: true,
          expectedDayCount: 0,
          error: null,
        }),
        isLoading: false,
      },
    },
  })),
  on(AutoOrderActions.scheduleDataLoadingFailed, (state: AutoOrderingState, {storeId, error}) => ({
    ...state,
    isAutoOrderScheduleLoading: false,
    scheduleLoadingState: {
      ...state.scheduleLoadingState,
      [storeId]: {
        ...(state.scheduleLoadingState[storeId] || {
          isLoading: false,
          daysLoaded: [],
          suppliersLoaded: false,
          expectedDayCount: 0,
          error: null,
        }),
        isLoading: false,
        error,
      },
    },
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.cleanupAutoOrderingScheduleSettings, (state: AutoOrderingState, {storeId}) => {
    const newScheduleLoadingState = {...state.scheduleLoadingState};
    delete newScheduleLoadingState[storeId];

    return {
      ...state,
      scheduleLoadingState: newScheduleLoadingState,
    };
  }),
  // ====================================================================================================
  // Prepare Auto Order For Submission - NEW ACTIONS
  // ====================================================================================================
  on(AutoOrderActions.prepareAutoOrderForSubmission, (state: AutoOrderingState, {order}) => ({
    ...state,
    prepareOrderOutcome: null,
    preparingOrderIds: state.preparingOrderIds.includes(order?.orderId)
      ? state.preparingOrderIds
      : [...state.preparingOrderIds, order?.orderId],
  })),
  on(AutoOrderActions.prepareAutoOrderForSubmissionSuccess, (state: AutoOrderingState, {storeId, orderId}) => {
    const existingOrders = state.autoOrderOrders[storeId] || [];

    const storeOrders: IAutoOrderOrder[] = existingOrders.map((order: IAutoOrderOrder): IAutoOrderOrder =>
      order.orderId === orderId
        ? {...order, status: 'PREPARED'}
        : order,
    );

    return {
      ...state,
      prepareOrderOutcome: {orderId, succeeded: true},
      preparingOrderIds: state.preparingOrderIds.filter((id: string): boolean => id !== orderId),
      autoOrderOrders: {
        ...state.autoOrderOrders,
        [storeId]: storeOrders,
      },
    };
  }),
  on(AutoOrderActions.prepareAutoOrderForSubmissionFailure, (state: AutoOrderingState, {error, orderId}) => ({
    ...state,
    prepareOrderOutcome: {orderId, succeeded: false},
    preparingOrderIds: state.preparingOrderIds.filter((id: string): boolean => id !== orderId),
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.clearPrepareAutoOrderOutcome, (state: AutoOrderingState) => ({
    ...state,
    prepareOrderOutcome: null,
  })),
  on(AutoOrderActions.setStockItemDisabledFromOrder, (state: AutoOrderingState) => ({
    ...state,
    isUpdatingStockItemDisabled: true,
  })),
  on(
    AutoOrderActions.setStockItemDisabledFromOrderSuccess,
    (state: AutoOrderingState, {code, enable, fieldUpdates, previousValues}) => {
      const baselines: Record<string, Record<string, StockItemUpdateFieldValue>> = {...state.stockItemDisableBaselines};
      if (enable) {
        delete baselines[code];
      } else if (previousValues) {
        baselines[code] = {...(baselines[code] ?? {}), ...previousValues};
      }

      return {
        ...state,
        isUpdatingStockItemDisabled: false,
        stockItemDisableBaselines: baselines,
        stockItemDisabledOutcome: {code, enable, fieldUpdates},
      };
    },
  ),
  on(AutoOrderActions.clearStockItemDisabledFromOrderResult, (state: AutoOrderingState) => ({
    ...state,
    stockItemDisabledOutcome: null,
  })),
  on(AutoOrderActions.setStockItemDisabledFromOrderFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isUpdatingStockItemDisabled: false,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.updateStockItemOnHold, (state: AutoOrderingState) => ({
    ...state,
    isUpdatingStockItemOnHold: true,
    stockItemOnHoldUpdateFailed: false,
  })),
  on(AutoOrderActions.updateStockItemOnHoldSuccess, (state: AutoOrderingState) => ({
    ...state,
    isUpdatingStockItemOnHold: false,
    stockItemOnHoldUpdateFailed: false,
  })),
  on(AutoOrderActions.updateStockItemOnHoldFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isUpdatingStockItemOnHold: false,
    stockItemOnHoldUpdateFailed: true,
    errors: [...state.errors, error],
  })),
  on(AutoOrderActions.createStockTakeFromUncountedOrderItems, (state: AutoOrderingState) => ({
    ...state,
    isCreatingOrderStockTake: true,
    createdOrderStockTakeId: null,
  })),
  on(AutoOrderActions.createStockTakeFromUncountedOrderItemsSuccess, (state: AutoOrderingState, {stockTakeId}) => ({
    ...state,
    isCreatingOrderStockTake: false,
    createdOrderStockTakeId: stockTakeId,
  })),
  on(AutoOrderActions.createStockTakeFromUncountedOrderItemsFailure, (state: AutoOrderingState, {error}) => ({
    ...state,
    isCreatingOrderStockTake: false,
    errors: [...(state.errors ?? []), error],
  })),
);

/**
 * Keeps a notification's order in the loaded list when the default query does not return it.
 */
let pendingOpenAutoOrderRequest = 0;

function mergeAutoOrderIntoStoreList(
  orders: IAutoOrderOrder[],
  order: IAutoOrderOrder,
): IAutoOrderOrder[] {
  const hasOrder: boolean = orders.some((candidate: IAutoOrderOrder): boolean => candidate.orderId === order.orderId);
  if (!hasOrder) {
    return orders;
  }
  return orders.map((candidate: IAutoOrderOrder): IAutoOrderOrder =>
    candidate.orderId === order.orderId ? order : candidate,
  );
}

function mergeRetriedAutoOrderIntoOpenOrder(
  openOrder: Record<string, IOpenAutoGeneratedOrder>,
  order: IAutoOrderOrder,
): Record<string, IOpenAutoGeneratedOrder> {
  const existing: IOpenAutoGeneratedOrder | undefined = openOrder[order.orderId];
  if (!existing) {
    return openOrder;
  }
  return {
    ...openOrder,
    [order.orderId]: {
      ...existing,
      orderDetails: order,
    },
  };
}

function applyRetryAutoOrderSuccess(state: AutoOrderingState): AutoOrderingState {
  const orderId: string | null = state.pendingRetryAutoOrderId;
  if (!orderId) {
    return state;
  }

  const patchOrderInList = (orders: IAutoOrderOrder[]): IAutoOrderOrder[] =>
    orders.map((order: IAutoOrderOrder): IAutoOrderOrder =>
      order.orderId === orderId ? patchAutoOrderAfterRetrySubmit(order) : order,
    );

  const autoOrderOrders: Record<string, IAutoOrderOrder[]> = Object.fromEntries(
    Object.entries(state.autoOrderOrders).map(([storeId, orders]: [string, IAutoOrderOrder[]]) => [
      storeId,
      patchOrderInList(orders),
    ]),
  );

  const openEntry: IOpenAutoGeneratedOrder | undefined = state.openOrder[orderId];
  let patchedOrder: IAutoOrderOrder | undefined = openEntry?.orderDetails
    ? patchAutoOrderAfterRetrySubmit(openEntry.orderDetails)
    : undefined;

  if (!patchedOrder) {
    for (const orders of Object.values(autoOrderOrders)) {
      patchedOrder = orders.find((order: IAutoOrderOrder): boolean => order.orderId === orderId);
      if (patchedOrder) {
        break;
      }
    }
  }

  const openOrder: Record<string, IOpenAutoGeneratedOrder> = openEntry && patchedOrder
    ? {
      ...state.openOrder,
      [orderId]: {
        ...openEntry,
        orderDetails: patchedOrder,
      },
    }
    : state.openOrder;

  const pendingRetryAutoOrderId: string | null = shouldClearPendingRetryAutoOrder(
    orderId,
    patchedOrder ? [patchedOrder] : [],
  )
    ? null
    : orderId;

  return {
    ...state,
    autoOrderOrders,
    openOrder,
    pendingRetryAutoOrderId,
  };
}

function mergePendingOpenOrder(
  orders: IAutoOrderOrder[],
  pending: AutoOrderingState['pendingOpenAutoOrder'],
  storeId: string,
): IAutoOrderOrder[] {
  if (!pending?.order || pending.storeId !== storeId) {
    return orders;
  }
  if (orders.some((order: IAutoOrderOrder) => order.orderId === pending.orderId)) {
    return orders;
  }
  return [...orders, pending.order];
}

/**
 * Copies a refreshed order onto the open grid so retry eligibility follows Firestore.
 */
function syncRetriedOpenOrder(
  openOrder: Record<string, IOpenAutoGeneratedOrder>,
  orders: IAutoOrderOrder[],
  pendingOrderId: string | null,
): Record<string, IOpenAutoGeneratedOrder> {
  if (!pendingOrderId || !openOrder[pendingOrderId]) {
    return openOrder;
  }
  const refreshed: IAutoOrderOrder | undefined = orders.find(
    (order: IAutoOrderOrder): boolean => order.orderId === pendingOrderId,
  );
  if (!refreshed) {
    return openOrder;
  }
  return {
    ...openOrder,
    [pendingOrderId]: {
      ...openOrder[pendingOrderId],
      orderDetails: refreshed,
    },
  };
}

export const autoOrderingReducer = (state: AutoOrderingState, action: Action): AutoOrderingState => createAutoOrderingReducer(state, action);
